import type { ReactElement } from 'react';
import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Pressable } from '../../primitives/Pressable';
import type {
  BottomSheetContextValue,
  BottomSheetHandle,
  BottomSheetRootProps,
} from './BottomSheet.types';

const BottomSheetContext = createContext<BottomSheetContextValue | null>(null);

/** Internal channel: hands `BottomSheetRoot`'s ref down to the sheet itself. */
interface BottomSheetInternalValue {
  sheetRef: { current: BottomSheetHandle | null };
  onDismiss: () => void;
}
const BottomSheetInternalContext = createContext<BottomSheetInternalValue | null>(null);

/** @internal — used by `BottomSheet` to adopt its Root's ref. */
export function useBottomSheetInternalContext() {
  return useContext(BottomSheetInternalContext);
}

/**
 * Open / close / `isOpen` for the nearest {@link BottomSheetRoot}. Throws
 * outside a Root — a trigger with no sheet to open is always a wiring bug.
 */
export function useBottomSheetContext(): BottomSheetContextValue {
  const context = useContext(BottomSheetContext);
  if (!context) {
    throw new Error('BottomSheet compound parts must be used inside <BottomSheetRoot>');
  }
  return context;
}

/**
 * The imperative handle of the nearest {@link BottomSheetRoot} —
 * `present` / `dismiss` / `expand` / `collapse` / `scrollToEnd`.
 *
 * @example
 * ```tsx
 * const sheet = useBottomSheetRef();
 * sheet.current?.expand();
 * ```
 */
export function useBottomSheetRef() {
  return useBottomSheetContext().sheetRef;
}

/**
 * Owns a sheet's open state and its imperative ref, so a `BottomSheetTrigger`
 * (or anything calling `useBottomSheetRef`) can drive a `BottomSheet` rendered
 * further down the tree without prop-drilling.
 *
 * Controlled (`open` + `onOpenChange`) and uncontrolled (trigger-driven) both
 * work; `BottomSheet` accepts `open`/`onOpenChange` directly too, so a Root is
 * only needed for the trigger / imperative patterns.
 *
 * @example
 * ```tsx
 * <BottomSheetRoot>
 *   <BottomSheetTrigger>
 *     <Button>Open</Button>
 *   </BottomSheetTrigger>
 *   <BottomSheet title="Options">…</BottomSheet>
 * </BottomSheetRoot>
 * ```
 */
export function BottomSheetRoot({
  children,
  open: controlledOpen,
  onOpenChange,
}: BottomSheetRootProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const sheetRef = useRef<BottomSheetHandle | null>(null);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  // Callers pass `onOpenChange` as an inline closure almost everywhere, so with
  // it in the dependency lists below `open`/`close` — and therefore BOTH memoised
  // context values — changed identity on every CALLER render, and every mounted
  // sheet's content re-rendered for a value-equal context (a WDYR capture counted
  // 72 sheet re-renders during one deck open, 64 of them value-equal,
  // 2026-08-27). The ref keeps the callbacks identity-stable while always
  // calling the caller's LATEST handler.
  const onOpenChangeRef = useRef(onOpenChange);
  useLayoutEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  });

  const open = useCallback(() => {
    sheetRef.current?.present();
    if (isControlled) onOpenChangeRef.current?.(true);
    else setUncontrolledOpen(true);
  }, [isControlled]);

  // Re-entry guard: the sheet's own close reports back here, so a close that
  // starts here would otherwise report `onOpenChange(false)` twice.
  const closing = useRef(false);
  const close = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    try {
      sheetRef.current?.dismiss();
      if (isControlled) onOpenChangeRef.current?.(false);
      else setUncontrolledOpen(false);
    } finally {
      closing.current = false;
    }
  }, [isControlled]);

  // useLayoutEffect, not useEffect: the child sheet assigns `sheetRef` during
  // ITS layout phase, which runs first, so the handle is already there.
  useLayoutEffect(() => {
    if (!isControlled) return;
    if (controlledOpen) sheetRef.current?.present();
    else sheetRef.current?.dismiss();
  }, [isControlled, controlledOpen]);

  const value = useMemo<BottomSheetContextValue>(
    () => ({ open, close, isOpen, sheetRef }),
    [open, close, isOpen],
  );
  const internal = useMemo<BottomSheetInternalValue>(
    () => ({ sheetRef, onDismiss: close }),
    [close],
  );

  return (
    <BottomSheetContext.Provider value={value}>
      <BottomSheetInternalContext.Provider value={internal}>
        {children}
      </BottomSheetInternalContext.Provider>
    </BottomSheetContext.Provider>
  );
}
BottomSheetRoot.displayName = 'BottomSheetRoot';

export interface BottomSheetTriggerProps {
  /**
   * The control that opens the sheet. An element (its `onPress` is cloned) or
   * a render function receiving `{ onPress }`.
   */
  children: ReactElement | ((props: { onPress: () => void }) => ReactElement);
  /**
   * Clone `onPress` onto the child instead of wrapping it in a Pressable.
   * Default `true` — wrapping a Button in another pressable breaks its
   * press states and doubles the hit target.
   */
  asChild?: boolean;
}

/** Opens the nearest {@link BottomSheetRoot}'s sheet when pressed. */
export function BottomSheetTrigger({
  children,
  asChild = true,
}: BottomSheetTriggerProps) {
  const { open } = useBottomSheetContext();

  if (typeof children === 'function') return children({ onPress: open });

  if (asChild && isValidElement(children)) {
    return cloneElement(children as ReactElement<{ onPress?: () => void }>, {
      onPress: open,
    });
  }

  return (
    <Pressable as="div" onPress={open}>
      {Children.only(children)}
    </Pressable>
  );
}
BottomSheetTrigger.displayName = 'BottomSheetTrigger';
